import React, { useState, useEffect } from 'react';

import { Popover } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  Campaign,
  Character,
  CharacterAdvantage,
  CharacterClass,
  Money,
  RolledValue,
  SKILLS,
  Spell,
} from "@dndModels";
import { COST_UNIT_MAP, TIME_UNIT_MAP, TIME_UNIT_MAP_SINGULAR } from "@dndModels/Constants";

import API from "@utilities/smart_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";
import { makeStyles } from "@utilities/theme_utilities";

import SpellDetails from "@dndComponents/display/SpellDetails";
import ViewSpell from "@dndComponents/display/ViewSpell";
import ButtonBox from "@components/input/buttons/ButtonBox";
import DandDButtonBox from "@dndComponents/DandDButtonBox";
import Roller from "@dndComponents/other/Roller";
import CharConnectUtilities from '@dndUtilities/char_connect_utilities';

interface Props {
  char: Character | Campaign;
  spell: Spell;
  expanded: boolean;
  onChange: () => void;
  onExpand: () => void;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

/**
 * Making it so that Books can have data stored in them.  
 * Book Types: Spell, Creature, Item, Other.
 * Spell Books work kind of like a collection of Scrolls, but can only be used for learning, not casting, but don't get used up in the process.
 * Creature Books and Item Books are just used to record and read info about them.
 * Other is the standard one, and it just has a place for the owner to record whatever they want.
 */
export default function CharacterSpellBookRowInput({
  char,
  spell,
  expanded,
  onChange,
  onExpand
}: Props) {
  const [reloading, setReloading] = useState(false);
  const [ping, setPing] = useState(false);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLDivElement | null>(null);
  const [roll, setRoll] = useState<RolledValue[]>([]);
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main
      },
    });
  });

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const { classes, cx } = useStyles();
  const enabled = api.user && (char.owner_id === api.user._id || (char instanceof Character && char.campaign && char.campaign.owner_id === api.user._id));

  const addSpell = (char_class: CharacterClass, spell: Spell) => {
    if (char instanceof Character) {
      connect_util.addSpellToBook(char, spell, char_class, true);
      char.changed();
      char.subscription_holder.recalculate();
      onChange();
    }
  }
  
  const renderExtras = () => {
    return (
      <>
        <Popover
          open={ popoverAnchorEl !== null }
          anchorEl={popoverAnchorEl}
          onClose={() => {
            setPopoverAnchorEl(null);
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          { popoverAnchorEl !== null && renderRoll() }
        </Popover>
      </>
    );
  }
  
  const renderRoll = () => {
    // Rolls an Arcana check, and tells you if you succeeded
    const skill = SKILLS.find(o => o.name === "Arcana");
    const dc = 10 + spell.level;

    if (skill) {
      return (
        <Roller 
          name={skill.name}
          rolled_values={roll} 
          type="Skill Check" 
          dc={dc}
        />
      );
    }
  }

  const renderScrollLikeControls = () => {
    if (char instanceof Character){
      const spellbook_classes = char.spirit.classes.filter(o => o.spellbook && 
        o.spell_level_max >= spell.level &&
        o.spellbook.spellbook && common_util.anyOverlaps(o.spell_list_ids, spell.spell_list_ids));
      if (enabled && spellbook_classes.length > 0) {
        return (
          <Grid
            container
            spacing={0}
            direction="row"
            style={{ backgroundColor: "black" }}
            size={{
              xs: 12
            }}>
            { spellbook_classes.map((char_class, key) => {
              const char_spellbook = char_class.spellbook;
              if (char_spellbook && char_spellbook.spells.filter(o => o.spell_id === spell._id).length === 0) {
                const mods = char_spellbook.modifiers.filter(o => o.schools.length === 0 || o.schools.includes(spell.school));
                let time = char_spellbook.time_num_per_spell_level;
                let time_unit = char_spellbook.time_unit;
                let time_unit_str = "";
                let cost = char_spellbook.cost_per_spell_level;
                let cost_unit = char_spellbook.cost_unit;
                for (const mod of mods) {
                  if (mod.time_unit < time_unit) {
                    time = mod.time_num_per_spell_level;
                    time_unit = mod.time_unit;
                  } else if (mod.time_unit === time_unit && mod.time_num_per_spell_level < time) {
                    time = mod.time_num_per_spell_level;
                  }
                  if (mod.cost_unit < cost_unit) {
                    cost = mod.cost_per_spell_level;
                    cost_unit = mod.cost_unit;
                  } else if (mod.cost_unit === cost_unit && mod.cost_per_spell_level < cost) {
                    cost = mod.cost_per_spell_level;
                  }
                }
          
                cost *= spell.level;
                time *= spell.level;
                if (time === 1) {
                  time_unit_str = TIME_UNIT_MAP_SINGULAR[time_unit];
                } else {
                  time_unit_str = TIME_UNIT_MAP[time_unit];
                }
          
                const skill = SKILLS.find(o => o.name === "Arcana");
                const dc = 10 + spell.level;
                let arcana_string = "+0";
                let use_advantage = 0;
                let use_advantages: CharacterAdvantage[] = [];
                let use_gr_adv = false;
          
                if (skill) {
                  const { bonuses_string } = char_util.skillRolls(char, skill);
                  const { advantage, advantages, greater_advantage } = char_util.getAdvantage(char, "Ability Check", skill);
          
                  arcana_string = bonuses_string;
                  use_advantage = advantage;
                  use_advantages = advantages;
                  use_gr_adv = greater_advantage;
                }
          
                const cost2 = new Money();
                switch (cost_unit) {
                  case 1: cost2.copper = cost; break;
                  case 2: cost2.silver = cost; break;
                  case 3: cost2.electrum = cost; break;
                  case 4: cost2.gold = cost; break;
                  case 5: cost2.platinum = cost; break;
                }
                return (
                  <Grid
                    key={key}
                    container
                    spacing={0}
                    direction="row"
                    size={{
                      xs: 12
                    }}>
                    <Grid
                      size={{
                        xs: 6
                      }}>
                      <ButtonBox
                        name={ `Add for ${cost} ${COST_UNIT_MAP[cost_unit]}` }
                        disabled={ !enabled || char.money.gold_value() < cost2.gold_value() }
                        onClick={() => {
                          // Removes cost, and learns spell
                          char.money.subtract(cost2);
                          addSpell(char_class, spell);
                        }}
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 6
                      }}>
                      <DandDButtonBox
                        name={`Arcana Check: ${arcana_string}, DC ${dc}`}
                        advantage={ use_advantage }
                        advantages={ use_advantages }
                        greater_advantage={ use_gr_adv }
                        onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                          // const spell = item.extra_spell;
                          // Rolls an Arcana check, and tells you if you succeeded
                          const skill = SKILLS.find(o => o.name === "Arcana");
                          // let dc = 10 + spell.level;
                    
                          if (skill) {
                            const { skill_rolls: rolls } = char_util.skillRolls(char, skill);
                            const { advantage, greater_advantage } = char_util.getAdvantage(char, "Ability Check", skill);
                            const minimum_roll = char_util.getMinimumRoll(char, "Ability Check", skill);
                            // const greater_advantage = character.greater_advantages.filter(o => (o.roll_types.length === 0 || o.roll_types.includes("Ability Check")) && skill.use_ability_score !== "None" && (o.use_ability_scores.length === 0 || o.use_ability_scores.includes(skill.use_ability_score))).length > 0;
                            setRoll(char_util.rollD20({ char: char, rolls, type: "Ability Check", use_ability_score: skill.use_ability_score, advantage, greater_advantage, minimum_roll }));
                          }
                          setPopoverAnchorEl(event.currentTarget);
                        }}
                      />
                    </Grid>
                    <Grid
                      className="centered"
                      size={{
                        xs: 12
                      }}>
                      <span className='small_label gray'>{ `Takes ${time} ${time_unit_str}` }</span>
                    </Grid>
                  </Grid>
                );
              }
              return null;
            })}
            { renderExtras() }
          </Grid>
        );
      }
    }
    return null;
  }

  return (
    <Grid
      className="padded"
      size={{
        xs: 12
      }}>
      <Grid container spacing={0} direction="row" className={cx(classes.root)}>
        <Grid
          className='clickable'
          onClick={() => { 
            onExpand();
            setPing(!ping);
          }}
          size={{
            xs: 12
          }}>
          <ViewSpell
            spell={spell}
          />
        </Grid>
        { expanded &&
          <>
            <Grid
              size={{
                xs: 12
              }}>
              <SpellDetails 
                spell={spell}
              />
            </Grid>
            { renderScrollLikeControls() }
          </>
        }
      </Grid>
    </Grid>
  );
}
